import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GenderPreference, GenderOptions, Template, FavoritePhrase } from '../../types';
import { ModeSelector } from '../ModeSelector';
import {
  FrontendMode,
  DEFAULT_MODE,
  getModeConfig,
  mapFrontendMode,
} from '../../config/translationModes';
import { supabaseService } from '../../services/supabaseClient';
import { favoritesService } from '../../services/favoritesService';
import './TranslationForm.css';

interface TranslationFormProps {
  onTranslate: (params: {
    text: string;
    sourceLang: string;
    targetLang: string;
    context: string;
    genderPreferences: GenderOptions;
    mode?: string;
  }) => Promise<void>;
  isLoading: boolean;
  selectedTemplate?: Template | null;
}

const MAX_CHARS = 5000;

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'he', name: 'Hebrew' },
  { code: 'ar', name: 'Arabic' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ko', name: 'Korean' },
];

const GENDER_OPTIONS: { value: GenderPreference; label: string }[] = [
  { value: 'masculine', label: 'Masculine' },
  { value: 'feminine', label: 'Feminine' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'inclusive', label: 'Inclusive' },
];

// Session storage keys
const STORAGE_KEYS = {
  MODE: 'translayshun_mode',
  CONTEXT: 'translayshun_context',
  SOURCE_GENDER: 'translayshun_source_gender',
  TARGET_GENDER: 'translayshun_target_gender',
  SOURCE_LANG: 'translayshun_source_lang',
  TARGET_LANG: 'translayshun_target_lang',
};

export const TranslationForm: React.FC<TranslationFormProps> = ({ onTranslate, isLoading, selectedTemplate }) => {
  const [mode, setMode] = useState<FrontendMode>(() => {
    return (sessionStorage.getItem(STORAGE_KEYS.MODE) as FrontendMode) || DEFAULT_MODE;
  });
  const [text, setText] = useState('');
  const [sourceLang, setSourceLang] = useState(() => {
    return sessionStorage.getItem(STORAGE_KEYS.SOURCE_LANG) || 'en';
  });
  const [targetLang, setTargetLang] = useState(() => {
    return sessionStorage.getItem(STORAGE_KEYS.TARGET_LANG) || 'es';
  });

  // Get the current mode's context options
  const modeConfig = getModeConfig(mode);
  const modeContextOptions = modeConfig.contextOptions;

  const [context, setContext] = useState<string>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.CONTEXT);
    // Validate stored context is valid for current mode
    if (stored && modeContextOptions.some((opt) => opt.id === stored)) {
      return stored;
    }
    return modeContextOptions[0]?.id || 'casual';
  });
  const [sourceGender, setSourceGender] = useState<GenderPreference | ''>(() => {
    return (sessionStorage.getItem(STORAGE_KEYS.SOURCE_GENDER) as GenderPreference) || '';
  });
  const [targetGender, setTargetGender] = useState<GenderPreference | ''>(() => {
    return (sessionStorage.getItem(STORAGE_KEYS.TARGET_GENDER) as GenderPreference) || '';
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  // Favorites insert panel state
  const [showFavorites, setShowFavorites] = useState(false);
  const [favorites, setFavorites] = useState<FavoritePhrase[]>([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const [hasAuth, setHasAuth] = useState(false);

  // Load favorites when panel is first opened
  const loadFavorites = useCallback(async () => {
    try {
      const { user } = await supabaseService.getCurrentUser();
      if (!user) {
        setHasAuth(false);
        return;
      }
      setHasAuth(true);
      const data = await favoritesService.getFavorites(user.id);
      setFavorites(data);
      setFavoritesLoaded(true);
    } catch {
      // Silently fail — favorites panel is optional
      setFavoritesLoaded(true);
    }
  }, []);

  // Check auth on mount to decide whether to show the toggle button
  useEffect(() => {
    supabaseService.getCurrentUser().then(({ user }) => {
      setHasAuth(!!user);
    }).catch(() => {});
  }, []);

  // Load favorites when panel is toggled open
  useEffect(() => {
    if (showFavorites && !favoritesLoaded) {
      loadFavorites();
    }
  }, [showFavorites, favoritesLoaded, loadFavorites]);

  // Check session storage for a phrase inserted from FavoritesPage
  useEffect(() => {
    const favoriteText = sessionStorage.getItem('translayshun_favorite_text');
    if (favoriteText) {
      setText(favoriteText);
      sessionStorage.removeItem('translayshun_favorite_text');
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, []);

  const handleFavoriteClick = (phrase: FavoritePhrase) => {
    setText(phrase.text);
    setShowFavorites(false);
    // Increment usage count (fire and forget)
    favoritesService.incrementUsage(phrase.id).catch(console.error);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  // When mode changes, reset context to first option of new mode if current is invalid
  useEffect(() => {
    const newModeConfig = getModeConfig(mode);
    const validIds = newModeConfig.contextOptions.map((o) => o.id);
    if (!validIds.includes(context)) {
      setContext(validIds[0] || 'casual');
    }
  }, [mode]);

  // Apply template when selected
  useEffect(() => {
    if (selectedTemplate) {
      setSourceLang(selectedTemplate.sourceLang);
      setTargetLang(selectedTemplate.targetLang);
      setContext(selectedTemplate.context);
      setSourceGender(selectedTemplate.genderPreferences.sourceGender || '');
      setTargetGender(selectedTemplate.genderPreferences.targetGender || '');

      // Optionally populate sample text
      if (selectedTemplate.sampleText && !text) {
        setText(selectedTemplate.sampleText);
      }

      // Focus on text input
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [selectedTemplate]);

  // Persist preferences
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.MODE, mode);
  }, [mode]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.CONTEXT, context);
  }, [context]);

  useEffect(() => {
    if (sourceGender) sessionStorage.setItem(STORAGE_KEYS.SOURCE_GENDER, sourceGender);
  }, [sourceGender]);

  useEffect(() => {
    if (targetGender) sessionStorage.setItem(STORAGE_KEYS.TARGET_GENDER, targetGender);
  }, [targetGender]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.SOURCE_LANG, sourceLang);
  }, [sourceLang]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.TARGET_LANG, targetLang);
  }, [targetLang]);

  // Mobile keyboard handling - scroll textarea into view when focused
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handleFocus = () => {
      // Small delay to allow keyboard to appear
      setTimeout(() => {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    };

    textarea.addEventListener('focus', handleFocus);
    return () => textarea.removeEventListener('focus', handleFocus);
  }, []);

  // Touch gesture support for swapping languages
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (isLoading) return;
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (isLoading) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;

      // Detect horizontal swipe (more horizontal than vertical movement)
      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 100;

      if (isHorizontalSwipe) {
        // Swap languages on horizontal swipe
        handleSwapLanguages();
      }
    };

    form.addEventListener('touchstart', handleTouchStart);
    form.addEventListener('touchend', handleTouchEnd);

    return () => {
      form.removeEventListener('touchstart', handleTouchStart);
      form.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isLoading, sourceLang, targetLang, sourceGender, targetGender]);

  const charCount = text.length;
  const isOverLimit = charCount > MAX_CHARS;
  const isValid = text.trim().length > 0 && !isOverLimit;

  // Add dynamic class for selected state
  const getSelectClass = (value: string) => {
    return value ? 'has-value' : '';
  };

  const handleSwapLanguages = () => {
    const tempLang = sourceLang;
    const tempGender = sourceGender;
    setSourceLang(targetLang);
    setSourceGender(targetGender);
    setTargetLang(tempLang);
    setTargetGender(tempGender);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;

    await onTranslate({
      text: text.trim(),
      sourceLang,
      targetLang,
      context,
      genderPreferences: {
        sourceGender: sourceGender || 'neutral',
        targetGender: targetGender || 'neutral',
      },
      mode: mapFrontendMode(mode),
    });
  };

  // Current mode's context options for rendering
  const currentContextOptions = getModeConfig(mode).contextOptions;
  const currentPlaceholder = getModeConfig(mode).placeholder;

  return (
    <form className="translation-form" onSubmit={handleSubmit} ref={formRef}>
      {/* Mode Selector */}
      <ModeSelector
        selectedMode={mode}
        onModeChange={setMode}
        disabled={isLoading}
      />

      {/* Language and Gender Selection Row */}
      <div className="language-gender-row">
        <div className="language-section">
          <label className="section-label">From</label>
          <div className="controls-group">
            <select
              className={`language-select ${getSelectClass(sourceLang)}`}
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
              disabled={isLoading}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
            <select
              className={`gender-select ${getSelectClass(sourceGender)}`}
              value={sourceGender}
              onChange={(e) => setSourceGender(e.target.value as GenderPreference)}
              disabled={isLoading}
            >
              <option value="">Gender</option>
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          className="swap-button"
          onClick={handleSwapLanguages}
          disabled={isLoading}
          aria-label="Swap languages"
          title="Swap languages and genders (or swipe horizontally)"
        >
          ⇄
        </button>

        <div className="language-section">
          <label className="section-label">To</label>
          <div className="controls-group">
            <select
              className={`language-select ${getSelectClass(targetLang)}`}
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
              disabled={isLoading}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
            <select
              className={`gender-select ${getSelectClass(targetGender)}`}
              value={targetGender}
              onChange={(e) => setTargetGender(e.target.value as GenderPreference)}
              disabled={isLoading}
            >
              <option value="">Gender</option>
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Text Input */}
      <div className="text-section">
        <textarea
          ref={textareaRef}
          className={`text-input ${isOverLimit ? 'error' : ''} ${text.trim() ? 'has-value' : ''}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={currentPlaceholder}
          disabled={isLoading}
          rows={6}
        />
        <div className={`char-counter ${isOverLimit ? 'error' : ''}`}>
          {charCount} / {MAX_CHARS} characters
        </div>
      </div>

      {/* Favorites Insert Panel */}
      {hasAuth && (
        <div className="favorites-insert-panel">
          <button
            type="button"
            className={`favorites-insert-toggle ${showFavorites ? 'active' : ''}`}
            onClick={() => setShowFavorites(!showFavorites)}
            disabled={isLoading}
          >
            {showFavorites ? 'Hide Favorites' : 'Insert from Favorites'}
          </button>

          {showFavorites && (
            <div className="favorites-insert-list">
              {!favoritesLoaded ? (
                <p className="favorites-insert-loading">Loading...</p>
              ) : favorites.length === 0 ? (
                <p className="favorites-insert-empty">
                  No favorite phrases yet. Save phrases from the Favorites page.
                </p>
              ) : (
                favorites.map((phrase) => (
                  <button
                    key={phrase.id}
                    type="button"
                    className="favorites-insert-item"
                    onClick={() => handleFavoriteClick(phrase)}
                  >
                    <span className="favorites-insert-item__label">{phrase.label}</span>
                    <span className="favorites-insert-item__text">
                      {phrase.text.length > 60 ? phrase.text.slice(0, 60) + '...' : phrase.text}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Context Selection — driven by current mode */}
      <div className="context-section">
        <label className="section-label">Context</label>
        <div className="context-buttons">
          {currentContextOptions.map((ctx) => (
            <button
              key={ctx.id}
              type="button"
              className={`context-button ${context === ctx.id ? 'active' : ''}`}
              onClick={() => setContext(ctx.id)}
              disabled={isLoading}
              title={ctx.description}
            >
              {ctx.label}
            </button>
          ))}
        </div>
      </div>

      {/* Translate Button */}
      <button
        type="submit"
        className="translate-button"
        disabled={!isValid || isLoading}
      >
        {isLoading ? 'Translating...' : modeConfig.label}
      </button>
    </form>
  );
};
